import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, CheckCircle2, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (pw !== confirm) {
      toast.error("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed. Please request a new link.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="App">
        <Header />
        <div className="rgu-container py-20 max-w-2xl">
          <div className="border border-amber-200 bg-amber-50 p-8" data-testid="reset-missing-token">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-display text-lg text-[#0A192F]">Missing reset token</div>
                <p className="text-sm text-slate-700 mt-2">
                  This page should be opened via the link in your password reset email. If you&apos;ve lost the email, you can request a new one.
                </p>
                <Link to="/forgot-password" className="inline-block mt-4 text-xs font-mono-rgu uppercase tracking-widest text-[#0A192F] underline">
                  Request a new reset link
                </Link>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="App">
      <Header />
      <div className="rgu-container py-20 grid grid-cols-1 md:grid-cols-12 gap-12 max-w-4xl">
        <div className="md:col-span-5">
          <div className="rgu-eyebrow text-slate-500 mb-3">Account · Reset</div>
          <h1 className="font-display text-3xl md:text-4xl text-[#0A192F]">Set a new password.</h1>
          <p className="mt-4 text-slate-600">
            Choose a strong password (at least 8 characters). After saving, you&apos;ll be signed out of any active sessions and can sign in with the new password.
          </p>
        </div>

        {done ? (
          <div className="md:col-span-7 border border-slate-200 p-8" data-testid="reset-success">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-display text-lg text-[#0A192F]">Password updated</div>
                <p className="text-sm text-slate-600 mt-2">
                  Your password has been changed. Redirecting you to sign in…
                </p>
                <Link to="/login" className="inline-block mt-4 text-xs font-mono-rgu uppercase tracking-widest text-[#0A192F] underline">
                  Sign in now
                </Link>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="md:col-span-7 border border-slate-200 p-8 space-y-5" data-testid="reset-form">
            <div>
              <Label htmlFor="pw">New Password</Label>
              <Input
                id="pw"
                data-testid="reset-password"
                type="password"
                required
                minLength={8}
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                className="mt-2 rounded-none"
                placeholder="At least 8 characters"
              />
            </div>
            <div>
              <Label htmlFor="confirm">Confirm New Password</Label>
              <Input
                id="confirm"
                data-testid="reset-confirm"
                type="password"
                required
                minLength={8}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="mt-2 rounded-none"
                placeholder="Re-enter your new password"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="rgu-btn-primary w-full justify-center text-xs uppercase tracking-widest inline-flex items-center gap-2"
              data-testid="reset-submit"
            >
              <Lock className="w-4 h-4" />
              {loading ? "Updating…" : "Update Password"}
            </button>
            <div className="text-sm text-slate-500 pt-2">
              <Link to="/login" className="text-[#0A192F] underline" data-testid="reset-signin-link">
                Back to sign in
              </Link>
            </div>
          </form>
        )}
      </div>
      <Footer />
    </div>
  );
}
